import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { KizenTypography } from '__app/typography';
import { Button } from '@kizen/kds/Button';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import {
  StyledButtonsWrapper,
  StyledPageWrapper,
  StyledPaginationWrapper,
  StyledSearchInput,
  StyledTableScrollContainer,
  StyledTableToolbar,
  StyledTableWrapper,
} from 'pages/MCPServerSettings/styles';
import BasicTable from '__components/Tables/Basic';
import { TRow } from '__components/Kizen/Table';
import TablePagination from 'components/Tables/Big/TablePagination';
import {
  SERVERS_TABLE_ACTIONS,
  DEFAULT_SERVERS_PAGINATION_SETTINGS,
  MCP_SERVER_SETTINGS_PERMISSION_KEY,
} from 'pages/MCPServerSettings/constants';
import { useColumns } from './columns';
import { mcpServerListQuery, mcpServerStepOptionsQuery } from 'query/mcp';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import MCPServerService from 'services/MCPServerService';
import type {
  MCPServerConfigListItem,
  MCPServerConfigListParams,
} from '@kizen/api/mcp';
import { useToast } from '__components/ToastProvider';
import { getOriginalError } from '__services/AxiosService';
import { getErrorMessage } from '__hooks/useErrors';
import useCan from 'hooks/useCan';
import { usePaginationSettings } from 'hooks/usePaginationSettings';
import Input from '__components/Kizen/Input';
import { NewServerModal } from './NewServerModal';
import { DeleteServerModal } from './DeleteServerModal';

export const Servers = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [showToast, toastVariant] = useToast();
  const queryClient = useQueryClient();

  const canWriteSection = useCan({
    edit: 'sections',
    for: MCP_SERVER_SETTINGS_PERMISSION_KEY,
  });

  const canDeleteSection = useCan({
    remove: 'sections',
    for: MCP_SERVER_SETTINGS_PERMISSION_KEY,
  });

  const {
    page,
    pageSize,
    search,
    ordering,
    actualSearch,
    handleChangePage,
    handleChangePageSize,
    handleChangeSearch,
    headData,
  } = usePaginationSettings(DEFAULT_SERVERS_PAGINATION_SETTINGS);

  const [showNewServerModal, setShowNewServerModal] = useState(false);
  const [serverToDelete, setServerToDelete] =
    useState<MCPServerConfigListItem | null>(null);

  const queryOptions = useMemo(
    () =>
      mcpServerListQuery({
        params: {
          page,
          page_size: pageSize,
          search,
          ordering: ordering as MCPServerConfigListParams['ordering'],
        },
        placeholderData: keepPreviousData,
        staleTime: 0,
      }),
    [page, pageSize, search, ordering]
  );

  const { data, isLoading, isFetching } = useQuery(queryOptions);
  const loading = isLoading || isFetching;

  const onRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
    queryClient.invalidateQueries({
      queryKey: mcpServerStepOptionsQuery().queryKey,
    });
  }, [queryClient, queryOptions.queryKey]);

  const navigateToServer = useCallback(
    (serverId: string, subpage?: string) => {
      const path = subpage
        ? `/mcp-servers/${serverId}/${subpage}`
        : `/mcp-servers/${serverId}`;
      history.push(path);
    },
    [history]
  );

  const { mutateAsync: deleteServerMutation } = useMutation({
    mutationFn: (id: string) => MCPServerService.deleteMCPServer(id),
    onSuccess: () => onRefresh(),
  });

  const onSelectAction = useCallback(
    (
      { value }: { value: keyof typeof SERVERS_TABLE_ACTIONS },
      server: MCPServerConfigListItem
    ) => {
      if (value === SERVERS_TABLE_ACTIONS.view) {
        navigateToServer(server.id);
      } else if (value === SERVERS_TABLE_ACTIONS.delete) {
        setServerToDelete(server);
      }
    },
    [navigateToServer]
  );

  const handleDeleteConfirmed = useCallback(async () => {
    if (!serverToDelete) return;
    try {
      await deleteServerMutation(serverToDelete.id);
      showToast({
        message: t('MCP server deleted successfully'),
        variant: toastVariant.SUCCESS,
      });
      setServerToDelete(null);
    } catch (error) {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Failed to delete MCP server'),
        variant: toastVariant.FAILURE,
      });
    }
  }, [serverToDelete, deleteServerMutation, showToast, t, toastVariant]);

  const handleCreateServer = useCallback(
    async (
      name: string,
      description: string,
      sharingSettings: Record<string, unknown>
    ) => {
      try {
        const created = await MCPServerService.createMCPServer({
          name,
          description: description || undefined,
          sharing_settings: sharingSettings as any,
        });
        queryClient.invalidateQueries({
          queryKey: mcpServerStepOptionsQuery().queryKey,
        });
        showToast({
          message: t('MCP server created successfully'),
          variant: toastVariant.SUCCESS,
        });
        setShowNewServerModal(false);
        navigateToServer(created.id, 'tools');
      } catch (error) {
        showToast({
          message:
            getErrorMessage(getOriginalError(error)) ||
            t('Failed to create MCP server'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [navigateToServer, queryClient, showToast, t, toastVariant]
  );

  const columns = useColumns({
    onSelectAction,
    onClickName: (server) => navigateToServer(server.id),
    onClickTools: (server) => navigateToServer(server.id, 'tools'),
    onClickSharingSettings: (server) =>
      navigateToServer(server.id, 'team-sharing'),
    canWriteSection,
    canDeleteSection,
  });

  return (
    <StyledPageWrapper>
      <StyledTableToolbar>
        <StyledSearchInput
          pill
          type={Input.INPUT_TYPES.SEARCH}
          placeholder={t('Find MCP Servers')}
          value={actualSearch}
          onChange={handleChangeSearch}
          data-qa-id="mcp-servers-search-input"
        />
        <KizenTypography as="h2" type="header">
          {t('{{count}} MCP {{instance}}', {
            count: data?.count ?? 0,
            instance: data?.count === 1 ? t('Server') : t('Servers'),
          })}
        </KizenTypography>
        <StyledButtonsWrapper>
          {canWriteSection ? (
            <Button
              onClick={() => setShowNewServerModal(true)}
              data-qa-id="new-mcp-server-button"
            >
              {t('New MCP Server')}
            </Button>
          ) : null}
        </StyledButtonsWrapper>
      </StyledTableToolbar>
      <StyledTableWrapper>
        <StyledTableScrollContainer bottom top gradient={<Gradient />}>
          <BasicTable
            stickyHeader
            head={<TRow head columns={columns} data={headData} />}
            staleData={loading}
          >
            {data?.count ? null : <TRow />}
            {data?.results.map((server: MCPServerConfigListItem) => (
              <TRow key={server.id} columns={columns} data={server} />
            ))}
          </BasicTable>
        </StyledTableScrollContainer>
      </StyledTableWrapper>
      <StyledPaginationWrapper>
        <TablePagination
          page={page}
          perPage={pageSize}
          totalCount={data?.count}
          onChangePage={handleChangePage}
          onChangePerPage={handleChangePageSize}
        />
      </StyledPaginationWrapper>

      {showNewServerModal ? (
        <NewServerModal
          onConfirm={handleCreateServer}
          onHide={() => setShowNewServerModal(false)}
        />
      ) : null}

      {serverToDelete ? (
        <DeleteServerModal
          server={serverToDelete}
          onConfirm={handleDeleteConfirmed}
          onHide={() => setServerToDelete(null)}
        />
      ) : null}
    </StyledPageWrapper>
  );
};
